import React from 'react';

import { makeId, plural } from '@reckon-web/utils';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { LoadingDots } from '@reckon-web/loading';
import { Button } from '@reckon-web/button';
import { PlusIcon } from '@reckon-web/icon';

import { dateCompare, maybeify, stringCompare } from '../../../utils/sort';
import { Task, useQueryTasks } from '../../../hooks/use-query-tasks';
import { TaskStatusBadges } from '../../common/task-status-badge';
import { NoTasks, NoTasksFound } from '../../work/tasks/Tasks';
import { KanbanOrList } from '../../work/kanban-or-list';
import { SortType } from '../../work/sort';
import { CreateTaskOptions } from '../../task/create-drawer';

import { KanbanView } from './kanban-view';
import { ListView } from './list-view';

const compareByDueAsc = (a: Task, b: Task) =>
  maybeify(dateCompare)(a.dueDate, b.dueDate);
const compareByDueDesc = (a: Task, b: Task) =>
  maybeify(dateCompare, false)(a.dueDate, b.dueDate);
const compareByNameAsc = (a: Task, b: Task) =>
  maybeify(stringCompare)(a.name, b.name);
const compareByNameDesc = (a: Task, b: Task) =>
  maybeify(stringCompare, false)(a.name, b.name);
const comparersBySortType: Record<SortType, ((a: Task, b: Task) => number)[]> =
  {
    'Due Date (earliest first)': [compareByDueAsc, compareByNameAsc],
    'Due Date (latest first)': [compareByDueDesc, compareByNameDesc],
    'Name (A → Z)': [compareByNameAsc, compareByDueAsc],
    'Name (Z → A)': [compareByNameDesc, compareByDueDesc],
  };

export const Tasks: React.FC<{
  kanbanOrList: KanbanOrList;
  queryResult: ReturnType<typeof useQueryTasks>;
  filtered: boolean;
  clearFilter: () => void;
  sort: SortType;
  onClickTask?: (taskId: string) => void;
  onClickCreateTask?: (options?: CreateTaskOptions) => void;
  readonly?: boolean;
  testId?: string;
}> = ({
  kanbanOrList,
  queryResult,
  filtered: _filtered,
  clearFilter,
  sort,
  onClickTask,
  onClickCreateTask,
  readonly,
  testId = `tasks`,
}) => {
  const compare = React.useCallback(
    (a: Task, b: Task) => {
      const comparers = comparersBySortType[sort];
      for (const comparer of comparers) {
        const result = comparer(a, b);
        if (result !== 0) {
          return result;
        }
      }
      // got all the way to here, they must be equal
      return 0;
    },
    [sort]
  );

  const { loading, error, data } = queryResult;
  const [state, setState] = React.useState({
    data,
    filtered: _filtered,
  });
  React.useEffect(
    () => data && setState({ data, filtered: _filtered }),
    [data, _filtered]
  );

  // TODO
  if (error) return <div>Error!</div>;

  const { data: tasks, filtered } = state;

  return (
    <Flex
      direction="column"
      height="100%"
      flexGrow={1}
      data={{ testid: testId }}
    >
      <Flex alignItems="center" gap="small" paddingY="medium" flexShrink={0}>
        {loading ? (
          <LoadingDots
            label="loading"
            size="small"
            data-testid={makeId(testId, 'loading')}
          />
        ) : (
          tasks && (
            <Flex alignItems="center" gap="xsmall">
              <Text
                size="xsmall"
                color="muted"
                data={{ testid: makeId(testId, 'text-showing') }}
              >
                Showing {plural(tasks.length, 'task', 'tasks')}
                {filtered && (
                  <Text
                    inline
                    size="xsmall"
                    color="dim"
                    weight="medium"
                    data={{ testid: makeId(testId, 'text-filtered') }}
                  >
                    &nbsp;
                    {typeof filtered === 'string'
                      ? `(filtered - ${filtered})`
                      : '(filtered)'}
                  </Text>
                )}
              </Text>
            </Flex>
          )
        )}
        <Flex flexGrow={1} />
        {kanbanOrList === 'list' && tasks && (
          <TaskStatusBadges tasks={tasks} testId={makeId(testId, 'badge')} />
        )}
        {onClickCreateTask && (
          <Button
            label="New task"
            iconBefore={PlusIcon}
            size="small"
            weight="subtle"
            onClick={() => onClickCreateTask()}
            data-testid={makeId(testId, 'new-task')}
          />
        )}
      </Flex>
      {tasks &&
        (() => {
          if (tasks.length > 0)
            return (
              <Flex direction="column" flexGrow={1} overflow="auto">
                {kanbanOrList === 'kanban' && (
                  <KanbanView
                    tasks={tasks}
                    compare={compare}
                    onClickTask={onClickTask}
                    onClickCreateTask={onClickCreateTask}
                    readonly={readonly}
                    data-testid={makeId(testId, 'kanban-view')}
                  />
                )}
                {kanbanOrList === 'list' && (
                  <ListView
                    tasks={tasks}
                    readonly={readonly}
                    onClickTask={onClickTask}
                    data-testid={makeId(testId, 'list-view')}
                  />
                )}
              </Flex>
            );

          if (filtered) return <NoTasksFound clearFilters={clearFilter} />;

          return <NoTasks onClickCreateTask={onClickCreateTask} />;
        })()}
    </Flex>
  );
};
